import React from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash/fp';
import supportsLocalStorage from 'utils/supportsLocalStorage';
import { hasFeatureFlags } from 'utils/features';
import FeatureFlag from 'components/shared/FeatureFlag';
import * as actions from '../../actions/shared/index/filter';
import Filter from '../shared/index/filters';
import DefaultContentView from './DefaultContentView';

const ContentFilters = props => (
  <Filter.Toolbar>
    {props.hasWorkspacesFeature && props.hasMultipleWorkspaces && (
      <Filter.Select
        selectedValue={props.selectedWorkspace}
        selectCallback={props.filterUpdateWorkspace}
      >
        <option value="all">{t('workspaces.all_workspaces')}</option>
        {props.workspaces.map(({ id, name }) => (
          <option key={id || 'default'} value={id || 'default'}>
            {name}
          </option>
        ))}
      </Filter.Select>
    )}

    <Filter.Select
      selectedValue={props.selectedContentType}
      selectCallback={value => props.filterUpdateContentType(value)}
    >
      <option value="any">{t('contents.index.filters.all_content')}</option>
      <option value="lesson">{t('contents.index.filters.lessons')}</option>
      {!props.hasCertificationsFeature && (
        <option value="path">{t('contents.index.filters.paths')}</option>
      )}
      {props.hasCertificationsFeature && (
        <option value="path">
          {t('contents.index.filters.paths_and_certifications')}
        </option>
      )}
      <option value="fresh_content">
        {t('contents.index.filters.freshness')}
      </option>
      {props.hasOnDemandPractice && (
        <option value="practice_containers">
          {t('contents.index.filters.practice_scenarios')}
        </option>
      )}
      {!props.isAdmin && (
        <option value="distribution_only">
          {t('contents.index.filters.distribution_only')}
        </option>
      )}
    </Filter.Select>

    <Filter.MultiSelect
      data={props.tagsMetadata}
      label={{ plural: 'Tags', singular: 'Tag' }}
      sort={{ key: 'name', descending: false }}
      selected={props.selectedTags}
      applyCallback={selected => props.filterUpdateTags(selected)}
    />

    <Filter.DatePicker
      applyCallback={dateData => props.filterUpdateDate(dateData)}
      longLabel="Date Updated"
      startDate={props.selectedDate ? props.selectedDate.start : null}
      endDate={props.selectedDate ? props.selectedDate.end : null}
    />

    <Filter.Select
      label="Creator"
      selectedValue={props.selectedCreator}
      selectCallback={value => props.filterUpdateCreator(value)}
    >
      {!!props.selectedCreator && <option value="any">Everybody</option>}
      <option value={props.currentUserId}>My Content</option>
      <option disabled>─</option>
      {props.creators.map(user => (
        <option key={user.id} value={user.id}>
          {user.name}
        </option>
      ))}
    </Filter.Select>

    {/* Last Child does not fall under "Index-filtersContainer" */}
    <>
      <FeatureFlag
        features="advanced_content_search"
        fallback={
          <Filter.Search
            value={props.selectedQuery}
            submitCallback={query => props.filterUpdateQuery(query)}
          />
        }
      >
        {props.canSaveFilters && supportsLocalStorage && <DefaultContentView />}
      </FeatureFlag>
    </>
  </Filter.Toolbar>
);

const mapStateToProps = ({ filter, data: { meta } }) => ({
  selectedContentType: filter.type,
  selectedTags: filter.tags,
  selectedDate: filter.date,
  selectedCreator: filter.creator,
  selectedQuery: filter.query,
  selectedWorkspace: filter.workspace,
  tagsMetadata: meta.tags,
  creators: meta.creators,
  currentUserId: meta.currentUser.id,
  isAdmin: meta.currentUser.isAdmin,
  hasWorkspacesFeature: get('features.workspaces', meta),
  hasOnDemandPractice: get('features.on_demand_practice', meta),
  hasCertificationsFeature: hasFeatureFlags(['certifications']),
  hasMultipleWorkspaces: meta.workspaceOptions.length > 1,
  workspaces: meta.workspaceOptions,
});

const mapDispatchToProps = {
  filterUpdateContentType: actions.filterUpdateContentType,
  filterUpdateTags: actions.filterUpdateTags,
  filterUpdateDate: actions.filterUpdateDate,
  filterUpdateCreator: actions.filterUpdateCreator,
  filterUpdateQuery: actions.filterUpdateQuery,
  filterUpdateWorkspace: actions.filterUpdateWorkspace,
};

export default connect(mapStateToProps, mapDispatchToProps)(ContentFilters);
